import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

export default function Contact() {
    useReveal();
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [status, setStatus] = useState("");
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/contact", form);
            setStatus("Thank you — we'll be in touch within 24 hours.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch {
            setStatus("Something went wrong. Please try again.");
        }
        setSending(false);
        setTimeout(() => setStatus(""), 5000);
    };

    return (
        <div data-testid="contact-page">
            <PageHero
                testId="contact-hero"
                overline="Get in touch"
                title={
                    <>
                        Let&apos;s <span className="italic font-light">talk.</span>
                    </>
                }
                subtitle="Product feedback, press, partnerships, or just a hello — we read every note."
                size="lg"
            />

            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5 reveal">
                        <h3 className="font-serif text-2xl text-femcare-purple mb-6">Reach us directly</h3>
                        <div className="space-y-5">
                            {[
                                { icon: Mail,  label: "Email",   value: "support@femcare.life",  href: "mailto:support@femcare.life" },
                                { icon: Phone, label: "Support", value: "+91 80 4567 8901" },
                                { icon: MapPin, label: "Office", value: "Bengaluru, India" },
                            ].map((r) => (
                                <div key={r.label} className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-femcare-pink/15 flex items-center justify-center shrink-0">
                                        <r.icon className="w-4 h-4 text-femcare-pink" />
                                    </div>
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent">{r.label}</p>
                                        {r.href ? (
                                            <a href={r.href} className="text-femcare-purple hover:text-femcare-pink">{r.value}</a>
                                        ) : (
                                            <p className="text-femcare-purple">{r.value}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Press + Affiliate glass card */}
                        <div className="mt-12 glass rounded-2xl p-6 space-y-4">
                            <div>
                                <p className="font-serif text-lg text-femcare-purple mb-1">Press &amp; media</p>
                                <p className="text-sm text-femcare-purple/70">
                                    Write to{" "}
                                    <a className="underline text-femcare-pink" href="mailto:press@femcare.life">
                                        press@femcare.life
                                    </a>
                                    .
                                </p>
                            </div>
                            <div className="border-t border-femcare-border pt-4">
                                <p className="font-serif text-lg text-femcare-purple mb-1 flex items-center gap-2">
                                    <Handshake className="w-4 h-4 text-femcare-pink" />
                                    Affiliate partnerships
                                </p>
                                <p className="text-sm text-femcare-purple/70">
                                    Creators, clinicians &amp; publishers →{" "}
                                    <a className="underline text-femcare-pink" href="mailto:affiliate@femcare.life">
                                        affiliate@femcare.life
                                    </a>{" "}
                                    or{" "}
                                    <Link to="/affiliate" className="underline text-femcare-pink">
                                        explore the program
                                    </Link>
                                    .
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="md:col-span-7 reveal reveal-delay-1">
                        <form
                            onSubmit={submit}
                            className="card-editorial p-8 md:p-10 space-y-5"
                            data-testid="contact-form"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Name</label>
                                    <input
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                        data-testid="contact-name"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                        data-testid="contact-email"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Subject</label>
                                <input
                                    required
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                    className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink"
                                    data-testid="contact-subject"
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.2em] text-femcare-accent block mb-2">Message</label>
                                <textarea
                                    required
                                    rows={6}
                                    value={form.message}
                                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="w-full bg-femcare-cream border border-femcare-border rounded-xl px-4 py-3 focus:outline-none focus:border-femcare-pink resize-none"
                                    data-testid="contact-message"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={sending}
                                className="btn-primary magnet disabled:opacity-50"
                                data-testid="contact-submit"
                            >
                                {sending ? "Sending..." : "Send message"}
                                <Send className="w-4 h-4" />
                            </button>
                            {status && (
                                <p className="text-sm text-femcare-accent" data-testid="contact-status">
                                    {status}
                                </p>
                            )}
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
